import React, { useEffect, useId, useState } from 'react'
import { getMsal } from '../services/auth'
import { minimumRoles } from '../config/OnboardingConfig'

const TenantRolesModal = ({ open, onClose, tenant }) => {
  const headingId = useId()
  const [userSearch, setUserSearch] = useState('')
  const [expandedGroupIds, setExpandedGroupIds] = useState(() => new Set())

  const copyText = text => {
    try {
      navigator.clipboard.writeText(text)
    } catch (_) {}
  }

  useEffect(() => {
    const onKeyDown = e => {
      if (e.key === 'Escape') onClose?.()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [onClose])

  if (!open || !tenant) return null

  const missingRoles = Array.isArray(tenant?.tenantMissingRoles) ? tenant.tenantMissingRoles : []
  const groupMissing = Array.isArray(tenant?.groupMissingRoles) ? tenant.groupMissingRoles : []
  const assignedGroups = Array.isArray(tenant?.assignedGroups) ? tenant.assignedGroups : []
  const eligible = Array.isArray(tenant?.eligibleOnboarders) ? tenant.eligibleOnboarders : []

  // Resolve signed-in user ID and UPN/email for membership checks
  let currentUserUpn = ''
  let currentUserObjectId = ''
  try {
    const pca = getMsal()
    const account = pca.getActiveAccount() || pca.getAllAccounts()?.[0]
    currentUserUpn = account?.username?.toLowerCase?.() || ''
    // Try to derive the object id from idTokenClaims (oid)
    currentUserObjectId = account?.idTokenClaims?.oid || ''
  } catch (_) {}

  const checklist = `Tenant: ${tenant?.companyProfile?.companyName || ''}\nMissing roles:\n${missingRoles.join('\n')}`

  // Determine if current user can onboard and build a filtered eligible list without them
  const canCurrentUserOnboard = (() => {
    try {
      return eligible.some(u => {
        const uid = u?.id || ''
        if (currentUserObjectId && uid && uid === currentUserObjectId) return true
        const upn = (u?.userPrincipalName || u?.mail || '').toLowerCase()
        return currentUserUpn && upn && upn === currentUserUpn
      })
    } catch (_) {
      return false
    }
  })()

  const filteredEligible = eligible.filter(u => {
    const uid = u?.id || ''
    const upn = (u?.userPrincipalName || u?.mail || '').toLowerCase()
    if (currentUserObjectId && uid && uid === currentUserObjectId) return false
    if (currentUserUpn && upn && upn === currentUserUpn) return false
    return true
  })

  const searchQuery = userSearch.toLowerCase().trim()
  const visibleEligible = filteredEligible.filter(u => {
    if (!searchQuery) return true
    const dn = (u?.displayName || '').toLowerCase()
    const em = (u?.userPrincipalName || u?.mail || '').toLowerCase()
    return dn.includes(searchQuery) || em.includes(searchQuery)
  })

  return (
    <div className='fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6'>
      <div className='absolute inset-0 bg-black/50 backdrop-blur-sm' onClick={onClose} />
      <div
        role='dialog'
        aria-modal='true'
        aria-labelledby={headingId}
        className='relative z-10 w-full max-w-5xl rounded-2xl bg-white dark:bg-slate-900 shadow-2xl ring-1 ring-black/5 dark:ring-white/10'
      >
        <div className='flex items-start justify-between gap-4 border-b border-gray-200 dark:border-slate-800 px-6 py-4'>
          <div>
            <h2 id={headingId} className='text-lg sm:text-xl font-semibold text-gray-900 dark:text-white'>
              {tenant?.companyProfile?.companyName || 'Tenant'}
            </h2>
            <p className='mt-0.5 text-xs text-gray-500 dark:text-gray-400'>GDAP details and eligible onboarders</p>
          </div>
          <button
            aria-label='Close'
            className='shrink-0 rounded-md p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:text-white dark:hover:bg-slate-800'
            onClick={onClose}
          >
            {/* X icon */}
            <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='currentColor' className='h-5 w-5'>
              <path fillRule='evenodd' d='M4.293 4.293a1 1 0 0 1 1.414 0L10 8.586l4.293-4.293a1 1 0 1 1 1.414 1.414L11.414 10l4.293 4.293a1 1 0 0 1-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 0 1-1.414-1.414L8.586 10 4.293 5.707a1 1 0 0 1 0-1.414Z' clipRule='evenodd' />
            </svg>
          </button>
        </div>

        <div className='px-6 py-5 grid grid-cols-1 lg:grid-cols-5 gap-6 h-[70vh] min-h-0'>
          {/* Left/Main column */}
          <div className='space-y-6 overflow-y-auto pr-2 lg:col-span-3 min-h-0'>
            <section>
            <h3 className='text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3'>Tenant-Level Missing Roles</h3>
            {missingRoles.length === 0 ? (
              <p className='text-sm text-gray-500 dark:text-gray-400'>None</p>
            ) : (
              <ul className='flex flex-wrap gap-2'>
                {missingRoles.map(r => (
                  <li key={r} className='inline-flex items-center rounded-full bg-amber-100 text-amber-800 px-2.5 py-1 text-xs dark:bg-amber-700/30 dark:text-amber-100'>
                    {r}
                  </li>
                ))}
              </ul>
            )}
            {missingRoles.length > 0 && (
              <button
                className='mt-3 text-xs px-2.5 py-1.5 rounded-md border border-gray-200 hover:bg-gray-50 text-gray-700 dark:border-slate-700 dark:text-gray-200 dark:hover:bg-slate-800'
                onClick={() => copyText(missingRoles.join('\n'))}
              >
                Copy Roles
              </button>
            )}
            </section>

            <section>
            <h3 className='text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3'>Security Group Missing Roles</h3>
            {groupMissing.length === 0 ? (
              <p className='text-sm text-gray-500 dark:text-gray-400'>None</p>
            ) : (
              <ul className='flex flex-wrap gap-2'>
                {groupMissing.map(r => (
                  <li key={r} className='inline-flex items-center rounded-full bg-rose-100 text-rose-800 px-2.5 py-1 text-xs dark:bg-rose-700/30 dark:text-rose-100'>
                    {r}
                  </li>
                ))}
              </ul>
            )}
            {groupMissing.length > 0 && (
              <button
                className='mt-3 text-xs px-2.5 py-1.5 rounded-md border border-gray-200 hover:bg-gray-50 text-gray-700 dark:border-slate-700 dark:text-gray-200 dark:hover:bg-slate-800'
                onClick={() => copyText(groupMissing.join('\n'))}
              >
                Copy Group Roles
              </button>
            )}
            </section>

            <section>
            <h3 className='text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3'>Assigned Security Groups</h3>
            {assignedGroups.length === 0 ? (
              <p className='text-sm text-gray-500 dark:text-gray-400'>No security groups assigned</p>
            ) : (
              <ul className='space-y-4'>
                {assignedGroups.map(g => {
                  const roleIds = new Set((g.roles || []).filter(Boolean))
                  const membership = (g.groupMembers || []).some(m => {
                    const memberId = m.id || ''
                    if (currentUserObjectId && memberId) return memberId === currentUserObjectId
                    const upn = (m.userPrincipalName || m.mail || '').toLowerCase()
                    return currentUserUpn && upn && upn === currentUserUpn
                  })
                  return (
                    <li key={g.groupId} className='rounded-xl border border-gray-200 dark:border-slate-800 p-4'>
                      <div className='flex items-center justify-between gap-3'>
                        <div className='font-medium text-gray-900 dark:text-gray-100'>{g.groupName}</div>
                        <div className='flex items-center gap-2'>
                          <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs ${membership ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-700/30 dark:text-emerald-100' : 'bg-gray-100 text-gray-700 dark:bg-slate-700/40 dark:text-gray-200'}`}>
                            {membership ? 'You are a member' : 'You are not a member'}
                          </span>
                          <button
                            className='rounded-md px-2 py-0.5 text-xs border border-gray-200 text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:border-slate-700 dark:hover:bg-slate-800'
                            onClick={() => setExpandedGroupIds(prev => { const next = new Set(prev); if (next.has(g.groupId)) next.delete(g.groupId); else next.add(g.groupId); return next })}
                          >
                            {expandedGroupIds.has(g.groupId) ? 'Hide Users' : 'View Users'}
                          </button>
                        </div>
                      </div>
                      <div className='mt-3'>
                        <p className='text-xs text-gray-500 dark:text-gray-400 mb-2'>Required role coverage</p>
                        <ul className='flex flex-wrap gap-2'>
                          {minimumRoles.map(r => (
                            <li key={r.id} className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs ${roleIds.has(r.id) ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-700/30 dark:text-emerald-100' : 'bg-rose-100 text-rose-700 dark:bg-rose-700/30 dark:text-rose-100'}`}>
                              {r.displayName}
                            </li>
                          ))}
                        </ul>
                      </div>
                      {expandedGroupIds.has(g.groupId) && (
                        <div className='mt-4 rounded-lg border border-gray-200 dark:border-slate-800'>
                          <div className='px-3 py-2 text-xs font-medium text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-slate-800'>Group Members</div>
                          <ul className='max-h-64 overflow-y-auto'>
                            {(g.groupMembers || []).map(m => {
                              const email = (m.userPrincipalName || m.mail || '').toLowerCase()
                              return (
                                <li key={m.id || email} className='px-3 py-2 flex items-center justify-between gap-3'>
                                  <div className='min-w-0'>
                                    <div className='text-sm text-gray-900 dark:text-gray-100 truncate'>{m.displayName || email || 'User'}</div>
                                    {email && <div className='text-xs text-gray-500 dark:text-gray-400 truncate'>{email}</div>}
                                  </div>
                                </li>
                              )
                            })}
                          </ul>
                        </div>
                      )}
                    </li>
                  )
                })}
              </ul>
            )}
            </section>
          </div>

          {/* Right/Sidebar column */}
          <aside className='lg:col-span-2 pl-2 flex flex-col min-h-0'>
            <div className='rounded-xl border border-gray-200 dark:border-slate-800 p-4 h-full flex flex-col min-h-0'>
              {/* Current user access card */}
              <div className='mb-4 rounded-lg border border-gray-200 dark:border-slate-800 p-3'>
                <div className='flex items-center justify-between gap-3'>
                  <div className='min-w-0'>
                    <div className='text-sm font-medium text-gray-900 dark:text-gray-100 truncate'>Your access</div>
                    <div className='text-xs text-gray-500 dark:text-gray-400 truncate'>{currentUserUpn || 'Signed in user'}</div>
                  </div>
                  <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs ${canCurrentUserOnboard ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-700/30 dark:text-emerald-100' : 'bg-rose-100 text-rose-700 dark:bg-rose-700/30 dark:text-rose-100'}`}>
                    {canCurrentUserOnboard ? "You can onboard" : "You can't onboard"}
                  </span>
                </div>
              </div>

              <div className='flex items-center justify-between mb-3'>
                <h3 className='text-sm font-semibold text-gray-800 dark:text-gray-200'>Eligible Onboarders</h3>
                {visibleEligible.length > 0 && (
                  <button
                    className='text-xs px-2.5 py-1.5 rounded-md border border-gray-200 hover:bg-gray-50 text-gray-700 dark:border-slate-700 dark:text-gray-200 dark:hover:bg-slate-800'
                    onClick={() => copyText(visibleEligible.map(u => (u.userPrincipalName || u.mail || u.displayName || '')).filter(Boolean).join('\n'))}
                  >
                    Copy Emails
                  </button>
                )}
              </div>
              {filteredEligible.length === 0 ? (
                <p className='text-sm text-gray-500 dark:text-gray-400'>No users currently meet all required roles.</p>
              ) : (
                <>
                  <div className='mb-3'>
                    <input
                      type='text'
                      value={userSearch}
                      onChange={e => setUserSearch(e.target.value)}
                      placeholder='Search users by name or email'
                      className='w-full rounded-md border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-inforcer-cyan'
                    />
                  </div>
                  <div className='flex-1 min-h-0 overflow-y-auto'>
                    {visibleEligible.length === 0 ? (
                      <p className='text-sm text-gray-500 dark:text-gray-400'>No users match your search.</p>
                    ) : (
                      <ul className='divide-y divide-gray-100 dark:divide-slate-800 rounded-xl border border-gray-200 dark:border-slate-800'>
                        {visibleEligible.map(u => {
                          const email = (u.userPrincipalName || u.mail || '').toLowerCase()
                          return (
                            <li key={u.id} className='p-3 sm:p-4 flex items-center justify-between gap-3'>
                              <div className='min-w-0'>
                                <div className='text-sm font-medium text-gray-900 dark:text-gray-100 truncate'>{u.displayName || email || 'User'}</div>
                                {email && <div className='text-xs text-gray-500 dark:text-gray-400 truncate'>{email}</div>}
                              </div>
                              <div className='shrink-0 flex items-center gap-2'>
                                <button
                                  className='rounded-md px-2.5 py-1 text-xs border border-gray-200 text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:border-slate-700 dark:hover:bg-slate-800'
                                  onClick={() => copyText(email || u.displayName || '')}
                                >
                                  Copy
                                </button>
                                {email && (
                                  <a
                                    href={`mailto:${email}`}
                                    className='rounded-md px-2.5 py-1 text-xs bg-inforcer-cyan text-white hover:opacity-90 no-select'
                                    onClick={e => e.stopPropagation()}
                                  >
                                    Email
                                  </a>
                                )}
                              </div>
                            </li>
                          )
                        })}
                      </ul>
                    )}
                  </div>
                </>
              )}
            </div>
          </aside>
        </div>

        <div className='flex items-center justify-between gap-3 border-t border-gray-200 dark:border-slate-800 px-6 py-4'>
          <div className='text-xs text-gray-500 dark:text-gray-400'>
            {missingRoles.length} tenant roles missing
            {groupMissing.length > 0 ? ` · ${groupMissing.length} group roles missing` : ''}
          </div>
          <div className='flex items-center gap-2'>
            <button
              className='rounded-md bg-inforcer-cyan text-white px-3 py-1.5 text-sm hover:opacity-90'
              onClick={onClose}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default TenantRolesModal


